import ky, { HTTPError } from 'ky';
import type { KyInstance } from 'ky';

// API base URL - pointing to the backend API
// Note: Ensure CORS is configured on the backend to allow requests from the admin app
// API base URL - pointing to the backend API
// Note: Ensure CORS is configured on the backend to allow requests from the admin app
// specific fix for local development where port might be 3001 in .env but API is running on 4000
const envUrl = import.meta.env.VITE_API_URL;
// Force port 4000 for local development if 3001 is detected or if undefined
// This handles the port mismatch issue strictly
const API_BASE_URL =
  envUrl && envUrl.includes('3001')
    ? 'http://localhost:4000/api'
    : envUrl
      ? envUrl
      : 'http://localhost:4000/api';

console.log('API Client init - VITE_API_URL:', envUrl);
console.log('API Client init - Resolved API_BASE_URL:', API_BASE_URL);

// Response types
interface AuthResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
  };
  token: string;
}

interface ErrorResponse {
  message: string;
  statusCode: number;
}

// Store CSRF token in memory
let csrfToken: string | null = null;

// Flag to prevent infinite retry loops
let isRefreshingCsrf = false;

// Create a Ky instance with default configuration
const createApiClient = (): KyInstance => {
  return ky.create({
    prefixUrl: API_BASE_URL,
    timeout: 30000, // 30 seconds
    credentials: 'include', // Include cookies for CORS requests
    retry: {
      limit: 2,
      methods: ['get', 'put', 'delete'],
      statusCodes: [408, 413, 429, 500, 502, 503, 504],
    },
    hooks: {
      beforeRequest: [
        (request) => {
          // Add auth token from storage if available
          const sessionToken = sessionStorage.getItem('adminToken');
          const persistedToken = localStorage.getItem('adminToken');
          const token = sessionToken || persistedToken;

          if (token) {
            request.headers.set('Authorization', `Bearer ${token}`);
          }

          // Add CSRF token for state-changing requests
          const method = request.method.toLowerCase();
          if (['post', 'put', 'patch', 'delete'].includes(method)) {
            if (csrfToken) {
              request.headers.set('X-CSRF-Token', csrfToken);
            }
          }
        },
      ],
      afterResponse: [
        async (request, options, response) => {
          const status = response.status;
          const url = response.url;

          // Handle 403 CSRF errors - refresh token and retry
          if (status === 403 && !isRefreshingCsrf) {
            try {
              const errorBody = (await response.clone().json()) as {
                error?: string;
              };
              if (errorBody.error?.toLowerCase().includes('csrf')) {
                isRefreshingCsrf = true;

                // Fetch new CSRF token
                const csrfResponse = await ky.get(
                  `${API_BASE_URL}/auth/csrf-token`,
                  {
                    credentials: 'include',
                    headers: {
                      Authorization: request.headers.get('Authorization') || '',
                    },
                  }
                );
                const csrfData = (await csrfResponse.json()) as {
                  csrfToken: string;
                };
                csrfToken = csrfData.csrfToken;

                isRefreshingCsrf = false;

                // Retry the original request with new CSRF token
                const retryRequest = new Request(request, {
                  headers: new Headers(request.headers),
                });
                retryRequest.headers.set('X-CSRF-Token', csrfToken);

                return ky(retryRequest, options);
              }
            } catch {
              isRefreshingCsrf = false;
            }
          }

          // Dispatch specific error events for global handling
          if (status >= 400 && status !== 401) {
            // 401 is handled specifically below
            const errorDetail = {
              status,
              url,
              message: response.statusText,
            };

            // Dispatch custom event for UI components to listen to
            window.dispatchEvent(
              new CustomEvent('api-error', { detail: errorDetail })
            );
          }

          // Handle 401 responses more carefully
          if (response.status === 401) {
            console.log('401 response from:', url);

            // Check if this is an auth endpoint (login, logout, me)
            const isAuthEndpoint = url.includes('/auth/');

            if (isAuthEndpoint) {
              // For auth endpoints, check if it's the /auth/me endpoint specifically
              const isMeEndpoint = url.includes('/auth/me');

              if (isMeEndpoint) {
                // 401 from /auth/me means token is invalid - clear tokens
                console.log(
                  '401 from /auth/me - token is invalid, clearing tokens'
                );
                sessionStorage.removeItem('adminToken');
                localStorage.removeItem('adminToken');

                // Only redirect if we're not already on the login page
                if (!window.location.pathname.includes('/login')) {
                  window.location.href = '/admin/login';
                }
              } else {
                // For login endpoint, 401 is expected for wrong credentials
                console.log('401 from login endpoint - invalid credentials');
              }
            } else {
              // For other endpoints, don't automatically logout
              // This could be a permission issue or business logic
              console.log(
                '401 from non-auth endpoint - not logging out automatically'
              );
            }
          }

          return response;
        },
      ],
    },
  });
};

// Export the API client instance
export const apiClient = createApiClient();

// Auth API endpoints
export const authApi = {
  login: async (email: string, password: string): Promise<AuthResponse> => {
    try {
      console.log('API Client - Login attempt:', {
        url: `${API_BASE_URL}/auth/login`,
        payload: { email, password: '***' },
      });

      const response = await apiClient.post('auth/login', {
        json: { email, password },
      });

      const data = await response.json<AuthResponse>();
      console.log('Login successful, received token');
      return data;
    } catch (error) {
      console.error('Login error caught:', error);

      if (error instanceof HTTPError) {
        const status = error.response.status;
        if (status === 401) {
          throw new Error('Invalid email or password');
        } else if (status === 500) {
          throw new Error('Server error. Please try again later.');
        }
      }

      throw new Error(
        'Login failed. Please check your connection and try again.'
      );
    }
  },

  logout: async (): Promise<void> => {
    try {
      await apiClient.post('auth/logout');
    } catch (error) {
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<ErrorResponse>();
        throw new Error(errorData.message || 'Logout failed');
      }
      throw error;
    }
  },

  getMe: async (): Promise<AuthResponse['user'] | null> => {
    try {
      const response = await apiClient.get('auth/me');
      const data = await response.json<{ user: AuthResponse['user'] }>();
      return data.user;
    } catch (error) {
      if (error instanceof HTTPError) {
        if (error.response.status === 401) {
          // Unauthorized - token is invalid
          return null;
        }
        if (error.response.status >= 500) {
          // Server error - don't clear tokens, just return null
          console.warn(
            'Server error during auth check:',
            error.response.status
          );
          throw new Error('Server temporarily unavailable');
        }
      }

      // For network errors or other issues, throw to trigger retry logic
      throw error;
    }
  },
};

// Users API endpoints (admin only)
export const usersApi = {
  create: async (userData: {
    email: string;
    password: string;
    name: string;
    role: string;
  }): Promise<AuthResponse['user']> => {
    try {
      const response = await apiClient.post('users', {
        json: userData,
      });

      return response.json<AuthResponse['user']>();
    } catch (error) {
      if (error instanceof HTTPError) {
        const errorData = await error.response.json<ErrorResponse>();
        throw new Error(errorData.message || 'Failed to create user');
      }
      throw error;
    }
  },
};

/**
 * Fetch CSRF token from server
 * Should be called after successful authentication
 */
export async function fetchCsrfToken(): Promise<void> {
  try {
    const response = await apiClient.get('auth/csrf-token');
    const data = await response.json<{ csrfToken: string }>();
    csrfToken = data.csrfToken;
    console.log('CSRF token fetched successfully');
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
    throw error;
  }
}

/**
 * Get the current CSRF token
 */
export function getCsrfTokenValue(): string | null {
  return csrfToken;
}

/**
 * Clear the CSRF token (call on logout)
 */
export function clearCsrfToken(): void {
  csrfToken = null;
}
